import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useId } from "react";
import { useMatchSets } from "../../api/queries";
import type {
  ConditionDto,
  FieldName,
  MatchSetId,
  Operator,
} from "../../api/types";
import { createDefaultCondition } from "../../logic/auto-transaction-form-logic";
import { summarizeCondition } from "../../logic/auto-transaction-summary";
import { useRuleSetNames } from "../../hooks/useRuleSetNames";
import { SortableList, moveItem, useSortableIds } from "../SortableList";
import { ConditionValueField } from "./ConditionValueField";
import { RetiredFieldChip } from "./RetiredFieldChip";
import { ConditionEntityHitsProvider } from "./ConditionEntityHitsProvider";
import {
  conditionForFieldChange,
  operatorLabel,
  stringOperators,
  valueForOperatorChange,
} from "./constants";

/**
 * A condition that defers to a named match set. The set's own condition is
 * shown read-only underneath so the rule can be read end to end without
 * opening the library — but it is *not* editable here: editing it would change
 * every other rule citing the same set, which is the one thing this indirection
 * exists to make deliberate.
 */
const MatchSetRefEditor = ({
  set,
  onChange,
}: {
  set: MatchSetId;
  onChange: (c: ConditionDto) => void;
}) => {
  const labelId = useId();
  const { data: sets } = useMatchSets();
  const resolvers = useRuleSetNames();
  const selected = sets?.find((s) => s.id === set);

  return (
    <Box sx={{ mb: 1 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={labelId}>Shared match</InputLabel>
        <Select
          labelId={labelId}
          label="Shared match"
          value={sets ? set : ""}
          onChange={(e) =>
            onChange({ type: "ref", set: e.target.value as MatchSetId })
          }
        >
          {(sets ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography
        variant="caption"
        component="pre"
        sx={{
          color: "text.secondary",
          m: 0,
          mt: 0.5,
          pl: 1,
          whiteSpace: "pre-wrap",
          borderLeft: "2px solid",
          borderColor: "divider",
        }}
      >
        {selected
          ? summarizeCondition(selected.condition, resolvers).full
          : set
            ? "This match set no longer exists — the rule cannot run until this is fixed."
            : "Pick a match set."}
      </Typography>
    </Box>
  );
};

/**
 * The rule editor's condition tree.
 *
 * Wraps the recursion in the entity-hits provider so every leaf can show what
 * it currently names in the registry; the tree is asked for in one request,
 * and each leaf looks its answer up by path.
 */
export const ConditionEditor = ({
  condition,
  onChange,
  isMobile,
}: {
  condition: ConditionDto;
  onChange: (c: ConditionDto) => void;
  isMobile: boolean;
}) => (
  <ConditionEntityHitsProvider condition={condition}>
    <ConditionNode
      condition={condition}
      onChange={onChange}
      isMobile={isMobile}
      path={[]}
    />
  </ConditionEntityHitsProvider>
);

const ConditionNode = ({
  condition,
  onChange,
  onUngroup,
  isMobile,
  path,
}: {
  condition: ConditionDto;
  onChange: (c: ConditionDto) => void;
  /**
   * Replaces this node with `conditions` in the enclosing group. Absent at the
   * root, whose slot holds exactly one condition.
   */
  onUngroup?: (conditions: ConditionDto[]) => void;
  isMobile: boolean;
  /** This node's child indices from the root of the tree. */
  path: number[];
}) => {
  if (condition.type === "ref") {
    return <MatchSetRefEditor set={condition.set} onChange={onChange} />;
  }

  if (condition.type === "retired") {
    // Read-only: nothing about it can be edited into something valid. In a
    // group the row's own delete button removes it; the root slot has no such
    // button, so it offers to start the condition over instead.
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 1 }}>
        <RetiredFieldChip field={condition.field} />
        {!onUngroup && (
          <Button
            size="small"
            onClick={() => onChange(createDefaultCondition())}
          >
            Replace
          </Button>
        )}
      </Stack>
    );
  }

  if (condition.type === "field") {
    return (
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 1 : 2}
        sx={{
          alignItems: isMobile ? "stretch" : "center",
          mb: 1,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 120 }} fullWidth={isMobile}>
          <Select
            value={condition.field}
            onChange={(e) => {
              const newField = e.target.value as FieldName;
              onChange({
                ...condition,
                field: newField,
                ...conditionForFieldChange(
                  condition.field,
                  newField,
                  condition.operator,
                  condition.value,
                ),
              });
            }}
          >
            <MenuItem value="source">Source</MenuItem>
            <MenuItem value="merchant">Merchant</MenuItem>
            <MenuItem value="description">Description</MenuItem>
            <MenuItem value="group">Group</MenuItem>
            <MenuItem value="expenseType">Expense Type</MenuItem>
            <MenuItem value="amount">Amount</MenuItem>
            <MenuItem value="datePaid">Date Paid</MenuItem>
            <MenuItem value="kid">KID</MenuItem>
            <MenuItem value="payToAccount">Pay To Account</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }} fullWidth={isMobile}>
          <Select
            value={condition.operator}
            onChange={(e) => {
              const next = e.target.value as Operator;
              onChange({
                ...condition,
                operator: next,
                value: valueForOperatorChange(
                  condition.operator,
                  next,
                  condition.value,
                ),
              });
            }}
          >
            {condition.field === "amount"
              ? [
                  <MenuItem key="equal" value="equal">
                    Equal
                  </MenuItem>,
                  <MenuItem key="greaterThan" value="greaterThan">
                    Above
                  </MenuItem>,
                  <MenuItem key="lessThan" value="lessThan">
                    Below
                  </MenuItem>,
                  <MenuItem key="between" value="between">
                    Between
                  </MenuItem>,
                ]
              : condition.field === "datePaid"
                ? [
                    <MenuItem key="onDay" value="onDay">
                      On Day
                    </MenuItem>,
                    <MenuItem key="betweenDays" value="betweenDays">
                      Between Days
                    </MenuItem>,
                  ]
                : stringOperators(condition.field, condition.operator).map(
                    (op) => (
                      <MenuItem key={op} value={op}>
                        {operatorLabel(condition.field, op)}
                      </MenuItem>
                    ),
                  )}
          </Select>
        </FormControl>
        <ConditionValueField
          condition={condition}
          onChange={onChange}
          path={path}
        />
        <Button
          size="small"
          onClick={() => onChange({ type: "all", conditions: [condition] })}
          sx={{ alignSelf: isMobile ? "flex-start" : "auto" }}
        >
          Group
        </Button>
      </Stack>
    );
  }

  return (
    <ConditionGroup
      condition={condition}
      onChange={onChange}
      onUngroup={onUngroup}
      isMobile={isMobile}
      path={path}
    />
  );
};

/**
 * An AND/OR group and its children.
 *
 * Its own component because the children are reorderable, and the ids that
 * survive a reorder are state — which the `ref`/`field` shapes above return
 * before ever needing.
 */
const ConditionGroup = ({
  condition,
  onChange,
  onUngroup,
  isMobile,
  path,
}: {
  condition: Extract<ConditionDto, { type: "all" | "any" }>;
  onChange: (c: ConditionDto) => void;
  onUngroup?: (conditions: ConditionDto[]) => void;
  isMobile: boolean;
  path: number[];
}) => {
  const ids = useSortableIds(condition.conditions.length);

  // Lifts the children out in place of the group. The root slot holds one
  // condition, so a root group can only ungroup while it has at most one
  // child; an empty one becomes a fresh leaf rather than nothing.
  const ungroup = () =>
    onUngroup
      ? onUngroup(condition.conditions)
      : onChange(condition.conditions[0] ?? createDefaultCondition());

  const addChild = (child: ConditionDto) => {
    ids.append();
    onChange({ ...condition, conditions: [...condition.conditions, child] });
  };

  return (
    <Box
      sx={{
        borderLeft: "2px solid #ccc",
        pl: isMobile ? 1 : 2,
        py: 1,
        mb: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 1,
        }}
      >
        <FormControl size="small">
          <Select
            value={condition.type}
            onChange={(e) =>
              onChange({
                ...condition,
                type: e.target.value as "all" | "any",
              })
            }
          >
            <MenuItem value="all">AND (All)</MenuItem>
            <MenuItem value="any">OR (Any)</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          disabled={!onUngroup && condition.conditions.length > 1}
          onClick={ungroup}
        >
          Ungroup
        </Button>
      </Stack>
      <SortableList
        ids={ids.ids}
        itemLabel="condition"
        spacing={0}
        onMove={(from, to) => {
          ids.move(from, to);
          onChange({
            ...condition,
            conditions: moveItem(condition.conditions, from, to),
          });
        }}
      >
        {(idx, handle) => (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ pt: 0.5 }}>{handle}</Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <ConditionNode
                condition={condition.conditions[idx]}
                onChange={(newSub) => {
                  const newConditions = [...condition.conditions];
                  newConditions[idx] = newSub;
                  onChange({ ...condition, conditions: newConditions });
                }}
                // A length change regenerates the sortable ids, which is the
                // hook's own fallback for a list replaced from outside.
                onUngroup={(lifted) =>
                  onChange({
                    ...condition,
                    conditions: [
                      ...condition.conditions.slice(0, idx),
                      ...lifted,
                      ...condition.conditions.slice(idx + 1),
                    ],
                  })
                }
                isMobile={isMobile}
                path={[...path, idx]}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                ids.remove(idx);
                onChange({
                  ...condition,
                  conditions: condition.conditions.filter((_, i) => i !== idx),
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </SortableList>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => addChild(createDefaultCondition())}
        >
          Add Condition
        </Button>
        <Button
          size="small"
          startIcon={<FilterAltIcon />}
          onClick={() =>
            // Empty until picked; the backend rejects a reference to a set that
            // doesn't exist, so a blank one can't be saved by mistake.
            addChild({ type: "ref", set: "" as MatchSetId })
          }
        >
          Use shared match
        </Button>
      </Stack>
    </Box>
  );
};
